import classnames from 'classnames';
import { UI, Utils as UtilsModule } from 'common';
import neutralizeCsvFormula from 'common/src/code/utils/neutralizeCsvFormula';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import React from 'react';
import AccountCommunication from '../utils/AccountCommunication';
import { RoutePath } from '../utils/RoutePath';
import AddSeatsModal from './AddSeatsModal.component';
import UserActionType from './UserActionType.enum';
import UserDetails from './UserDetails.component';
import UserLimitReachedModal from './UserLimitReachedModal.component';
import UsersGrid from './UsersGrid.component';
import UsersToolbar from './UsersToolbar.component';
import Utils from './Utils';

const {
	BillingPlans,
	Logger,
	Session,
	withLayoutAndModalContexts,
	UserRole: UserRoleEnum,
} = UtilsModule;
const { Spinner } = UI;

class Users extends React.Component {
	constructor(props) {
		super(props);

		const { userId } = Session.getAccountInfo();

		this.state = {
			userID: userId,
			corpID: null,
			users: [],
			selectedUsers: [],
			defaultUsers: [],
			regions: [],
			locations: [],
			action: '',
			filters: {
				Locations: [],
				Regions: [],
				Roles: [],
			},
			enableFiltersButton: false,
			isLoading: false,
			loadingMessage: '',
			maxAllowedUsers: null,
		};
		this.gridApi = null;
		this.isEnterpriseAccount = Utils.checkIsEnterpriseAdmin(
			Session.getUserRole()
		);
	}

	/**
	 * Display loading spinner based on `isLoading` boolean state
	 * @param {boolean} isLoading true to show Spinner component
	 * @param {string} loadingMessage
	 */
	displayLoadingSpinner = (isLoading, loadingMessage) => {
		this.setState({
			isLoading,
			loadingMessage,
		});
	};

	// fetch data for either enterprise user or non-enterprise user
	fetchData = callback => {
		if (this.isEnterpriseAccount) {
			this.fetchEnterpriseData(callback);
		} else {
			this.fetchNonEnterpriseData(callback);
		}
	};

	// fetch data for enterprise user
	fetchEnterpriseData = async callback => {
		const {
			enterpriseLocation,
			modalContext: { showError },
		} = this.props;
		const { userID } = this.state;
		const role = Session.getUserRole();
		const custID = this.getCustID();
		const response = {};
		const usersResponse = await AccountCommunication.getCorpUsers(custID);
		const locationsResponse = await AccountCommunication.getLocations();
		const userCorpInfoResponse =
			await AccountCommunication.getUserCorpInfo(userID);
		const regionsResponse =
			role === UserRoleEnum.CorpAdmin && !enterpriseLocation
				? await AccountCommunication.getRegions()
				: null;

		// process users response
		if (AccountCommunication.isSuccessful(usersResponse)) {
			response.users = this.processUsersData(usersResponse.data);
		} else {
			showError({ response: usersResponse });
		}

		// process locations response
		if (AccountCommunication.isSuccessful(locationsResponse)) {
			response.locations = isArray(locationsResponse.data)
				? locationsResponse.data.map(
						({ MeterAccount = {}, CustId, ParentCorpId }) => {
							const { Name, MeterNo, CorpId } = MeterAccount;
							return { Name, MeterNo, CorpId, CustId, ParentCorpId };
						}
					)
				: [];
		} else {
			showError({ response: locationsResponse });
		}

		// process userCorpInfo response
		if (AccountCommunication.isSuccessful(userCorpInfoResponse)) {
			response.corpID = isObject(userCorpInfoResponse.data)
				? userCorpInfoResponse.data.ParentCorpId ||
					userCorpInfoResponse.data.CorpId
				: null;
		} else {
			showError({ response: userCorpInfoResponse });
		}

		// process regions response
		if (role === UserRoleEnum.CorpAdmin && !enterpriseLocation) {
			if (AccountCommunication.isSuccessful(regionsResponse)) {
				response.regions = isArray(regionsResponse.data)
					? regionsResponse.data
					: [];
			} else {
				showError({ response: regionsResponse });
			}
		}

		// check if Filters buttons should be enabled
		if (
			role === UserRoleEnum.CorpAdmin &&
			isArray(response.regions) &&
			isArray(response.locations)
		) {
			response.enableFiltersButton =
				response.regions.length > 0 && response.locations.length > 0;
		} else if (
			role === UserRoleEnum.RegionAdmin &&
			isArray(response.locations)
		) {
			response.enableFiltersButton = response.locations.length > 0;
		}

		if (callback) {
			callback(response);
		}
	};

	// fetch data for non-enterprise user
	fetchNonEnterpriseData = async callback => {
		const {
			modalContext: { showError },
		} = this.props;
		const usersResponse = await AccountCommunication.getUsers();
		const response = {};

		// process users response
		if (AccountCommunication.isSuccessful(usersResponse)) {
			response.users =
				isObject(usersResponse.data) && isArray(usersResponse.data.Users)
					? this.processUsersData(usersResponse.data.Users)
					: [];
		} else {
			showError({ response: usersResponse });
		}

		if (callback) {
			callback(response);
		}
	};

	getUsers = async callback => {
		const {
			modalContext: { showError },
		} = this.props;
		const { locations } = this.state;
		const custID = this.getCustID();
		const response = this.isEnterpriseAccount
			? await AccountCommunication.getCorpUsers(custID)
			: await AccountCommunication.getUsers();

		if (AccountCommunication.isSuccessful(response)) {
			const users = this.isEnterpriseAccount
				? this.processUsersData(response.data)
				: this.processUsersData(response.data.Users);

			this.setState(
				{
					users,
					defaultUsers: users,
					locations: this.handleFilterLocation(locations, users),
				},
				() => {
					if (this.gridApi && this.gridApi.hideOverlay) {
						this.gridApi.hideOverlay();
					}

					if (callback) {
						callback();
					}
				}
			);
		} else {
			showError({ response });
		}
	};

	/**
	 * Return CustId for getCorpUsers endpoint.
	 * @returns {number}
	 */
	getCustID = () => {
		const { enterpriseLocation } = this.props;

		if (isObject(enterpriseLocation)) {
			return enterpriseLocation.CustId;
		} else {
			return 0;
		}
	};

	/**
	 * Process users data received from getUsers or getCorpUsers endpoint.
	 * @param {array} usersData Data received.
	 * @return {array}
	 */
	processUsersData = (usersData = []) => {
		const { history } = this.props;
		const { meterDetails = {} } = Session.getAccountInfo();
		const meterNumber = meterDetails.meterNumber || 0;

		const users = isArray(usersData)
			? usersData.map(userData => {
					const userCapabilities = isObject(userData.UserCapabilities)
						? userData.UserCapabilities
						: null;

					// check if per-print limit exceeds maximum value allowed
					const exceedPerPrintLimitMaximum =
						isObject(userCapabilities) &&
						isNumber(userCapabilities.PerPrintMaximumAmountLimit) &&
						userCapabilities.PerPrintMaximumAmountLimit >
							Utils.MAXIMUM_LIMIT_AMOUNT;

					return {
						...userData,
						UserCapabilities:
							exceedPerPrintLimitMaximum === true
								? {
										...userCapabilities,

										// per WEBAPPS-24338, per-print limit cannot exceed maximum
										PerPrintMaximumAmountLimit: Utils.MAXIMUM_LIMIT_AMOUNT,
									}
								: userData.UserCapabilities,
					};
				})
			: [];

		/* As per WEBAPPS-25726, user component will be used with RoutePath.users and RoutePath.locationUsers.
			RoutePath.locationUsers will display Users based on Account Location if meterNumber exists
		*/

		if (
			meterNumber &&
			history &&
			history.location.pathname === RoutePath.locationUsers
		) {
			return users.filter(
				user =>
					isObject(user.MeterAccount) &&
					user.MeterAccount.MeterNo &&
					user.MeterAccount.MeterNo === meterNumber
			);
		} else {
			return users;
		}
	};

	fetchMaxAllowedUsers = async () => {
		const response = await AccountCommunication.getMaxAllowedUsers();

		if (AccountCommunication.isSuccessful(response)) {
			this.setState({
				maxAllowedUsers: isNumber(response.data) ? response.data : null,
			});
		}
	};

	isAtSeatLimit = () => {
		const { users, maxAllowedUsers } = this.state;

		return isNumber(maxAllowedUsers) && users.length >= maxAllowedUsers;
	};

	handleAddUser = () => {
		if (this.isAtSeatLimit()) {
			this.showSeatLimitModal();
			return;
		}

		this.setState({
			action: UserActionType.Add,
		});
	};

	showSeatLimitModal = () => {
		const {
			modalContext: { showModal },
			history,
		} = this.props;
		const { maxAllowedUsers } = this.state;

		if (BillingPlans.isNewLineupPlan()) {
			showModal({
				component: AddSeatsModal,
				data: {
					maxSeats: maxAllowedUsers,
					onNavigate: history?.push.bind(history),
				},
			});
		} else {
			showModal({
				component: UserLimitReachedModal,
				data: {
					isEnterpriseAccount: this.isEnterpriseAccount,
				},
			});
		}
	};

	/**
	 * Format Add Funds,Reports, Store Enabled, Cost Codes Required value as Yes/No.
	 * @param {boolean} value
	 * @return {string}
	 */
	processUserCapsColumnValue = value => {
		if (value === undefined) {
			return '';
		}

		return value ? 'Yes' : 'No';
	};

	/**
	 * Exports the grid to a CSV file with header row. Note that all records are exported, not just selected rows.
	 */
	handleExportToCsv = () => {
		const { users } = this.state;
		const { customerDetails = {} } = Session.getAccountInfo();
		const username = customerDetails.username || '';

		Logger.saveActionData('Export Users Button Clicked');
		Logger.logToServer({
			category: 'ui_interaction',
			action: 'click',
			ui_element: 'export_button',
		});

		this.gridApi.exportDataAsCsv({
			fileName: `${username}Users.csv`,
			processCellCallback: ({ column: { colId }, value }) => {
				if (
					colId === 'AddFunds' ||
					colId === 'ViewReports' ||
					colId === 'BuySupplies' ||
					colId === 'CostCodesRequired' ||
					colId === 'EditCostCodes'
				) {
					return this.processUserCapsColumnValue(value);
				} else {
					return neutralizeCsvFormula(value);
				}
			},
		});

		Logger.logToServer({ category: 'export_users', users_count: users.length });
	};

	handleDeleteUser = async callback => {
		const { selectedUsers } = this.state;
		const {
			modalContext: { showError },
		} = this.props;
		const usernames = selectedUsers.map(user => user.UserName);
		const userIds = selectedUsers.map(user => user.UserId);

		this.displayLoadingSpinner(true, 'Updating users...');

		const response = this.isEnterpriseAccount
			? await AccountCommunication.deleteCorpUsers(userIds)
			: await AccountCommunication.deleteUsers(userIds);

		if (AccountCommunication.isSuccessful(response)) {
			this.getUsers();
			this.gridApi.deselectAll();
			Logger.logToServer({
				category: 'Delete Users',
				forUsernames: usernames,
			});
			this.setState({
				selectedUsers: [],
			});
			if (callback) {
				callback();
			}
		} else {
			showError({ response });
		}

		this.displayLoadingSpinner(false);
	};

	handleFilter = (newFilters, callback) => {
		const { defaultUsers } = this.state;
		const currentUserRole = Session.getUserRole();
		const newUsers = isEmpty(newFilters)
			? defaultUsers
			: defaultUsers.filter(user => {
					const { Name } = user.MeterAccount;
					const { CorpName, ParentCorpId } = user.UsersCorporationInformation;
					const role = Utils.getRoleUser(user).label;
					const result = [];

					if (newFilters.Locations && newFilters.Locations.length > 0) {
						result.push(newFilters.Locations.includes(Name));
					} else {
						return false;
					}

					if (
						currentUserRole === UserRoleEnum.CorpAdmin &&
						newFilters.Regions &&
						newFilters.Regions.length > 0
					) {
						const region = ParentCorpId ? CorpName : '';
						const regionIsValid =
							newFilters.Regions.includes(region) ||
							(newFilters.Regions.includes('No Region') &&
								/^\s*$/.test(region));

						result.push(regionIsValid);
					}

					if (newFilters.Roles && newFilters.Roles.length > 0) {
						result.push(newFilters.Roles.includes(role));
					}

					if (result.length === 0) {
						return false;
					}
					return !result.some(i => !i);
				});

		this.setState(
			{
				users: newUsers,
				filters: newFilters,
			},
			callback
		);
	};

	handleSearchUsers = (textSearch = '') => {
		const { defaultUsers } = this.state;

		if (this.gridApi) {
			this.gridApi.deselectAll();
		}

		// build regular expression object (case insensitive)
		const queryString = new RegExp(textSearch.trim(), 'i');
		const users = defaultUsers.filter(user => {
			const role = Utils.getRoleUser(user);
			const location = (user.MeterAccount && user.MeterAccount.Name) || '';
			const searchData = [
				user.FirstName,
				user.LastName,
				user.IsUserActivated ? 'Active' : 'Invited',
				role.label,
				location,
				`${user.FirstName} ${user.LastName}`,
			];

			return queryString.test(searchData);
		});

		this.setState({
			users,
			selectedUsers: [],
		});
	};

	handleSortUserGrid = sortModel => {
		this.gridApi.setSortModel(sortModel);
	};

	handleFilterLocation = (locations, users) => {
		const { enterpriseLocation } = this.props;
		if (
			locations &&
			locations.length > 0 &&
			users &&
			users.length &&
			this.isEnterpriseAccount &&
			!enterpriseLocation
		) {
			const userLocations = [];
			users.forEach(user => {
				if (
					user.MeterAccount &&
					user.MeterAccount.Name &&
					!userLocations.includes(user.MeterAccount && user.MeterAccount.Name)
				) {
					userLocations.push(user.MeterAccount.Name);
				}
			});

			return locations.filter(location =>
				userLocations.includes(location.Name)
			);
		}
		return locations;
	};

	isEnterpriseFilterEnabled = () => {
		const { regions, locations } = this.state;
		const role = Session.getUserRole();

		if (role === UserRoleEnum.CorpAdmin) {
			return regions.length > 0 && locations.length > 0;
		} else if (role === UserRoleEnum.RegionAdmin) {
			return locations.length > 0;
		}
	};

	onCloseUserDetails = async () => {
		await this.getUsers(() => {
			this.resetUserGrid();
		});
	};

	onFilterGridReady = ({ api }) => {
		this.filterGridApi = api;
	};

	onGridReady = ({ api }) => {
		this.gridApi = api;

		// show loading mask
		this.displayLoadingSpinner(true, 'Loading users...');

		// fetch seat limit
		this.fetchMaxAllowedUsers().catch(err => {
			Logger.logToServer({
				category: 'error_fetch_max_allowed_users',
				message: err?.message,
			});
		});

		// fetch data
		this.fetchData(response => {
			// filter available locations to display for enterprise account
			const responseLocations = this.handleFilterLocation(
				response.locations,
				response.users
			);

			this.setState(
				({ users, defaultUsers, regions, locations, corpID }) => ({
					users: isArray(response.users) ? response.users : users,
					defaultUsers: isArray(response.users) ? response.users : defaultUsers,
					regions: isArray(response.regions) ? response.regions : regions,
					locations: isArray(response.locations)
						? responseLocations
						: locations,
					corpID: response.corpID || corpID,
					enableFiltersButton: response.enableFiltersButton === true,
					isLoading: false,
					loadingMessage: '',
				}),
				() => this.gridApi.hideOverlay()
			);
		});
	};

	onSelectionChanged = () => {
		const selectedRows = this.gridApi.getSelectedRows();
		const numberUserSelected = selectedRows.length;
		this.setState({
			selectedUsers: selectedRows,
			action:
				numberUserSelected > 1
					? UserActionType.UpdateMultiUser
					: UserActionType.UpdateUser,
		});
	};

	resetUserGrid = () => {
		const { users } = this.state;

		if (this.gridApi) {
			this.gridApi.deselectAll();
		}

		this.setState({
			action: '',
			selectedUsers: [],
			users: [...users],
		});
	};

	render() {
		const {
			selectedUsers,
			users,
			action,
			userID,
			locations,
			regions,
			filters,
			corpID,
			enableFiltersButton,
			isLoading,
			loadingMessage,
			maxAllowedUsers,
		} = this.state;
		const { enterpriseLocation } = this.props;
		const currentPlan = BillingPlans.getCurrentActivePlan();
		const includedSeats = currentPlan?.NumberOfLocations ?? 1;
		const seatPrice = currentPlan?.AdditionalUserFee ?? 0;
		const purchasedSeats = isNumber(maxAllowedUsers)
			? Math.max(maxAllowedUsers - includedSeats, 0)
			: 0;
		// Derive whether the current user has authority to manage Account Owners.
		// Account Owners can always manage other Account Owners; Corp/Region Admins
		// can manage Account Owners within their corp/region scope (the backend
		// enforces the scope check).
		const currentUserData = users.find(u => u.UserId === userID);
		const isCurrentUserAccountOwner = Utils.isUserAccountOwner(currentUserData);
		const sessionRole = Session.getUserRole();
		const canManageAccountOwners =
			isCurrentUserAccountOwner ||
			sessionRole === UserRoleEnum.CorpAdmin ||
			sessionRole === UserRoleEnum.RegionAdmin;
		const showDetails =
			selectedUsers.length > 0 || action === UserActionType.Add;
		const wrapperCls = classnames('account-users account-users-wrapper', {
			'show-details': showDetails,
		});

		return (
			<div className={wrapperCls}>
				<UsersToolbar
					displayLoadingSpinner={this.displayLoadingSpinner}
					handleSearchUsers={this.handleSearchUsers}
					handleAddUser={this.handleAddUser}
					handleExportToCsv={this.handleExportToCsv}
					handleDeleteUser={this.handleDeleteUser}
					selectedUsers={selectedUsers}
					action={action}
					userID={userID}
					canManageAccountOwners={canManageAccountOwners}
					handleFilter={this.handleFilter}
					locations={locations}
					regions={regions}
					isEnterpriseAccount={this.isEnterpriseAccount}
					filters={filters}
					isEnableFilter={enableFiltersButton}
					enterpriseLocation={enterpriseLocation}
					usedSeats={users.length}
					maxSeats={maxAllowedUsers}
					includedSeats={includedSeats}
					purchasedSeats={purchasedSeats}
					seatPrice={seatPrice}
					onUpgradeComplete={this.fetchMaxAllowedUsers}
					history={this.props.history}
				/>
				<div className="grid-and-details-wrapper">
					<UsersGrid
						users={users}
						action={action}
						gridApi={this.gridApi}
						onGridReady={this.onGridReady}
						selectedUsers={selectedUsers}
						onSelectionChanged={this.onSelectionChanged}
						handleSortUserGrid={this.handleSortUserGrid}
						enterpriseLocation={enterpriseLocation}
						regions={regions}
						locations={locations}
					/>
					{showDetails && (
						<UserDetails
							displayLoadingSpinner={this.displayLoadingSpinner}
							resetUserGrid={this.resetUserGrid}
							action={action}
							selectedUsers={selectedUsers}
							onCloseUserDetails={this.onCloseUserDetails}
							regions={regions}
							locations={locations}
							corpID={corpID}
							enterpriseLocation={enterpriseLocation}
							maxSeats={maxAllowedUsers}
							history={this.props.history}
							canManageAccountOwners={canManageAccountOwners}
						/>
					)}
				</div>
				{isLoading && <Spinner msg={loadingMessage} />}
			</div>
		);
	}
}

export default withLayoutAndModalContexts(Users);
